import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/config";
import { useContent, C } from "@/hooks/useContent";
import { useSections, visible } from "@/hooks/useSections";
import { useEffect, useState } from "react";
import { ArrowRight, Check, MapPin, Star } from "lucide-react";
import BeforeAfter from "@/components/BeforeAfter";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const HERO_IMG_DEFAULT =
  "https://images.unsplash.com/photo-1543107076-8f86ee268706?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxwYWNpZmljJTIwbm9ydGh3ZXN0JTIwcGluZSUyMHRyZWVzJTIwZm9nfGVufDB8fHx8MTc4MDc5Mjk5NHww&ixlib=rb-4.1.0&q=85";
const ACTION_IMG_DEFAULT =
  "https://images.unsplash.com/photo-1718152421680-d1580e843cc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBjb25jcmV0ZXxlbnwwfHx8fDE3ODA3OTI5OTR8MA&ixlib=rb-4.1.0&q=85";
const AFTER_IMG_DEFAULT =
  "https://images.pexels.com/photos/8134845/pexels-photo-8134845.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PROCESS_STEPS = [
  { n: "01", k: "process.step1" },
  { n: "02", k: "process.step2" },
  { n: "03", k: "process.step3" },
];

export default function HomePage() {
  const content = useContent();
  const sections = useSections();
  useDocumentMeta({
    title: "Northwestern Exterior Cleaning · Pressure Washing in North Seattle",
    description: "North Seattle pressure washing & exterior cleaning. Book a free 30-minute on-site estimate — Marlowe measures your property and quotes in person.",
    path: "/",
  });

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={C(content, "home.hero_image_url", HERO_IMG_DEFAULT)} alt="Pacific Northwest forest mist" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/55 to-background" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="max-w-3xl fade-in">
            <span className="label-pill bg-white/85 backdrop-blur" data-testid="hero-eyebrow">
              <MapPin className="w-3 h-3" /> {C(content, "hero.eyebrow", "North Seattle, WA")}
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[0.95] mt-5 text-foreground">
              {C(content, "hero.title_line1", "Concrete,")}<br />
              <span className="italic text-primary">{C(content, "hero.title_line2", "restored.")}</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mt-5 max-w-xl leading-relaxed">
              {C(content, "hero.subtitle", "Northwestern Exterior Cleaning brings your driveways, walkways and patios back to life. We're built for Seattle's winter moss and grime.")}
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Link to="/booking">
                <Button data-testid="hero-book-btn" size="lg" className="btn-primary rounded-full px-7 h-12 text-base">
                  {C(content, "hero.cta_primary", "Book a cleaning")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button data-testid="hero-portfolio-btn" variant="outline" size="lg" className="rounded-full px-7 h-12 text-base border-foreground/15 bg-white/70 backdrop-blur">
                  {C(content, "hero.cta_secondary", "See past work")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      {visible(sections, "home.showcase") && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-10 lg:-mt-12 relative z-10" data-testid="showcase-section">
        <Card className="overflow-hidden p-0 border-border shadow-xl">
          <div className="grid lg:grid-cols-2">
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <p className="overline text-muted-foreground">{C(content, "showcase.eyebrow", "See the difference")}</p>
              <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-2 leading-tight">
                {C(content, "showcase.heading", "Drag to reveal what's underneath all that PNW grime.")}
              </h2>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                {C(content, "showcase.body", "Surface cleaners + carefully chosen detergents lift years of organic build-up without etching your concrete.")}
              </p>
              <div className="mt-6">
                <Link to="/portfolio">
                  <Button data-testid="showcase-portfolio-btn" variant="outline" className="rounded-full">
                    {C(content, "showcase.cta", "View full portfolio")} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="p-4 bg-muted">
              <BeforeAfter
                before={C(content, "home.showcase_before_url", ACTION_IMG_DEFAULT)}
                after={C(content, "home.showcase_after_url", AFTER_IMG_DEFAULT)}
                alt="driveway pressure wash"
              />
            </div>
          </div>
        </Card>
      </section>
      )}

      {/* Social proof — gray strip between Showcase and Quote, mirrors the Subscription bg */}
      <SocialProofBar content={content} />

      {/* Free estimate CTA */}
      {visible(sections, "home.quote") && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-16 lg:pt-20 lg:pb-20" data-testid="quote-section">
        <Card className="p-7 lg:p-10 bg-white border-border" data-testid="estimate-cta-card">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="min-w-0">
              <p className="overline text-muted-foreground">{C(content, "quote.eyebrow", "Free estimate · no obligation")}</p>
              <h3 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1 leading-tight">{C(content, "quote.heading", "Skip the guessing game.")}</h3>
              <p className="text-muted-foreground mt-3 max-w-md leading-relaxed">
                {C(content, "quote.body", "Most folks don't know their square footage — and you shouldn't have to. Book a 30-minute on-site visit and Marlowe will measure, walk the property with you, and quote in person.")}
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  C(content, "quote.bullet1", "30-minute visit · pick any afternoon slot"),
                  C(content, "quote.bullet2", "Firm price, in writing, before you commit"),
                  C(content, "quote.bullet3", "Zero pressure — just an honest quote"),
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-muted rounded-2xl p-7 min-w-0 flex flex-col justify-center">
              <p className="overline text-muted-foreground">{C(content, "quote.cta_eyebrow", "Book your visit")}</p>
              <p className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mt-2 leading-tight">
                {C(content, "quote.cta_heading", "Free · 30 min · in-person quote")}
              </p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {C(content, "quote.cta_body", "Marlowe brings a tape measure and answers every question. If you like the number, we schedule the wash on the spot.")}
              </p>
              <Link to="/booking" className="block mt-6">
                <Button data-testid="quote-book-btn" className="btn-primary w-full rounded-full h-12">
                  {C(content, "quote.cta_button", "Book a free estimate")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {C(content, "quote.cta_note", "No credit card · no obligation")}
              </p>
            </div>
          </div>
        </Card>
      </section>
      )}

      {/* How it works */}
      {visible(sections, "home.process") && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-16" data-testid="process-section">
        <p className="overline text-muted-foreground">{C(content, "process.eyebrow", "How it works")}</p>
        <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter mt-2 max-w-2xl">
          {C(content, "process.heading", "Three steps. No back-and-forth.")}
        </h2>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {PROCESS_STEPS.map((s) => (
            <div key={s.n} className="bg-white rounded-2xl p-7 border border-border hover-lift">
              <span className="font-display text-5xl font-light text-primary/30">{s.n}</span>
              <h3 className="font-display text-2xl font-semibold tracking-tight mt-3">{C(content, `${s.k}_title`, "")}</h3>
              <p className="text-muted-foreground mt-2 leading-relaxed text-sm">{C(content, `${s.k}_body`, "")}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* CTA */}
      {visible(sections, "home.cta") && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="bg-primary text-primary-foreground rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="relative max-w-2xl">
            <p className="overline text-white/60">{C(content, "cta.eyebrow", "Ready when you are")}</p>
            <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter mt-2 leading-tight">
              {C(content, "cta.heading", "Your driveway is one job away from looking brand new.")}
            </h2>
            <p className="text-white/80 mt-4 leading-relaxed">
              {C(content, "cta.body", "Booking takes under a minute. Available slots show in real time. Pick one and we'll handle the rest.")}
            </p>
            <Link to="/booking" className="inline-block mt-6">
              <Button data-testid="cta-final-book" size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full h-12 px-7">
                {C(content, "cta.button", "Book a slot")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

/** Trust strip between Showcase and Quote. Gray background mirrors the Subscription section.
 *  Combines:
 *    - Live booking count (auto, pulled from /api/social-proof)
 *    - CMS-driven review count + average rating (Marlowe edits these in Admin → Edit copy)
 *  Hides gracefully if both signals are zero so the page never embarrasses a brand-new business.
 */
function SocialProofBar({ content }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/social-proof").then(({ data }) => { if (alive) setData(data); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const reviewsCount = parseInt(C(content, "proof.reviews_count", "0"), 10) || 0;
  const reviewsAvg = parseFloat(C(content, "proof.reviews_avg", "5.0")) || 5.0;

  if (!data) return null;
  const { lifetime_jobs: total, last_30d_jobs: recent, area } = data;
  if (!total && !reviewsCount) return null;

  const useRecent = recent >= 5;
  const jobsNum = useRecent ? recent : total;
  const jobsWindow = useRecent ? "this month" : "completed";

  return (
    <section className="bg-muted/40 border-y border-border py-8" data-testid="social-proof-bar">
      <div
        data-testid="social-proof"
        className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-muted-foreground"
      >
        <span className="flex items-center gap-1 text-foreground" aria-label={`${reviewsAvg} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-current" strokeWidth={0} />
          ))}
          <span className="ml-1 font-medium">{reviewsAvg.toFixed(1)}</span>
        </span>
        {reviewsCount > 0 && (
          <span data-testid="reviews-count">
            <span className="font-medium text-foreground">{reviewsCount}</span>{" "}
            {reviewsCount === 1 ? "review" : "reviews"}
          </span>
        )}
        {total > 0 && (
          <span>
            <span data-testid="social-proof-count" className="font-medium text-foreground">{jobsNum}</span>{" "}
            {jobsNum === 1 ? "job" : "jobs"} {jobsWindow} in {area}
          </span>
        )}
      </div>
    </section>
  );
}
